import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { LOGO_URL } from "../../lib/api";
import { ALL_COMPANIES } from "../../context/CompanyContext";
import {
  LayoutDashboard, Users, FileText, FileSignature, GitBranch, LogOut,
  CalendarClock, CalendarDays, Building2, ChevronDown, UserCog, Layers,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

const navGroups = ({ isAdmin }) => [
  {
    label: "DoSoft",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
      ...(isAdmin
        ? [
            { to: "/admin/leads", label: "Leads", icon: Users },
            { to: "/admin/quotes", label: "Solicitudes", icon: FileText },
          ]
        : []),
      { to: "/admin/proposals", label: "Propuestas", icon: FileSignature },
    ],
  },
  {
    label: "Gestión por empresa",
    items: [
      { to: "/admin/crm", label: "CRM", icon: GitBranch },
      { to: "/admin/agenda", label: "Agenda", icon: CalendarClock },
      { to: "/admin/implementations", label: "Implementación", icon: CalendarDays },
    ],
  },
  ...(isAdmin
    ? [
        {
          label: "Administración",
          items: [
            { to: "/admin/admin/companies", label: "Empresas", icon: Building2 },
            { to: "/admin/users", label: "Vendedores", icon: UserCog },
            { to: "/admin/terms", label: "Términos", icon: FileText },
          ],
        },
      ]
    : []),
];

export function CompanySwitcher() {
  const { companies, active, activeId, isAll, setActiveId, loaded } = useCompany();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!loaded) return null;
  if (companies.length === 0) {
    // Sellers can't create companies — show inert pill instead.
    if (!isAdmin) {
      return (
        <div
          data-testid="company-switcher-noop"
          className="inline-flex items-center gap-2 border border-dashed border-ink-500 text-ash-500 px-4 py-2 text-xs uppercase tracking-widest"
          title="El administrador aún no te ha asignado empresas"
        >
          <Building2 size={14} /> Sin empresa asignada
        </div>
      );
    }
    return (
      <NavLink
        to="/admin/admin/companies"
        data-testid="company-switcher-empty"
        className="inline-flex items-center gap-2 border border-dashed border-ink-500 hover:border-brand text-ash-300 hover:text-brand px-4 py-2 text-xs uppercase tracking-widest"
      >
        <Building2 size={14} /> Configurar empresa
      </NavLink>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="company-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="h-11 inline-flex items-center gap-3 border border-ink-500 hover:border-brand bg-ink-700 px-3 min-w-[200px]"
      >
        {isAll ? (
          <Layers size={14} className="text-brand shrink-0" strokeWidth={1.8} />
        ) : (
          <span
            className="w-3 h-3 shrink-0"
            style={{
              background: active?.color || "#7BA6B0",
              boxShadow: `0 0 0 1px ${active?.color || "#7BA6B0"}, 0 0 8px ${active?.color || "#7BA6B0"}66`,
            }}
          />
        )}
        <div className="flex flex-col items-start leading-tight">
          <span className="text-[9px] uppercase tracking-widest text-ash-500 font-mono">Empresa</span>
          <span className="text-sm text-ash-100 font-medium truncate max-w-[170px]">
            {isAll ? "Todas las empresas" : (active ? active.name : "Selecciona…")}
          </span>
        </div>
        <ChevronDown size={14} className="ml-auto text-ash-300" />
      </button>
      {open && (
        <div data-testid="company-switcher-menu" className="absolute top-full left-0 mt-2 w-[280px] bg-ink-800 border border-ink-500 shadow-2xl z-50 max-h-96 overflow-y-auto">
          {isAdmin && (
            <button
              data-testid="company-option-all"
              onClick={() => { setActiveId(ALL_COMPANIES); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-ink-700 border-b border-ink-500/60 ${
                isAll ? "bg-brand/10" : ""
              }`}
            >
              <Layers size={14} className="text-brand shrink-0" strokeWidth={1.8} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ash-100">Todas las empresas</div>
                <div className="text-[11px] text-ash-500">Vista consolidada</div>
              </div>
            </button>
          )}
          {companies.map((c) => (
            <button
              key={c.id}
              data-testid={`company-option-${c.id}`}
              onClick={() => { setActiveId(c.id); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-ink-700 border-b border-ink-500/60 last:border-0 ${
                c.id === activeId ? "bg-brand/10" : ""
              }`}
            >
              <span
                className="w-3 h-3 shrink-0"
                style={{
                  background: c.color || "#7BA6B0",
                  boxShadow: `0 0 0 1px ${c.color || "#7BA6B0"}, 0 0 6px ${c.color || "#7BA6B0"}55`,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ash-100 truncate">{c.name}</div>
                {c.industry && <div className="text-[11px] text-ash-500 truncate">{c.industry}</div>}
              </div>
              {!c.is_active && <span className="text-[9px] uppercase tracking-widest text-ash-500">inactiva</span>}
            </button>
          ))}
          <NavLink
            to="/admin/admin/companies"
            onClick={() => setOpen(false)}
            className={`block px-4 py-3 text-[10px] uppercase tracking-widest border-t border-ink-500 ${
              isAdmin ? "text-brand hover:bg-ink-700" : "text-ash-500 cursor-not-allowed pointer-events-none opacity-50"
            }`}
          >
            {isAdmin ? "Gestionar empresas →" : "Asignadas por el administrador"}
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, checking, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const groups = navGroups({ isAdmin });

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-ink-900 text-ash-300">Cargando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-ink-900 flex">
      <aside className="w-64 border-r border-ink-500 bg-ink-800 flex flex-col relative">
        {/* Top accent stripe */}
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand via-brand to-brand-slate" />

        {/* Logo block — same height as topbar */}
        <div className="h-[73px] border-b border-ink-500 flex items-center justify-center px-6">
          <img
            src={LOGO_URL}
            alt="DoSoft"
            className="h-12 w-auto object-contain"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-6">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] uppercase tracking-widest text-ash-500 font-mono px-4 mb-2">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      data-testid={`admin-nav-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-all overflow-hidden ${
                          isActive
                            ? "bg-brand text-ink-800 font-semibold shadow-[inset_4px_0_0_0_#4A555C]"
                            : "text-ash-300 hover:bg-ink-700 hover:text-brand border-l-2 border-transparent hover:border-brand"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            size={16}
                            strokeWidth={1.6}
                            className={isActive ? "text-ink-800" : "text-brand"}
                          />
                          {item.label}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-auto flex flex-col">
        {/* Top bar with user profile */}
        <header
          data-testid="admin-topbar"
          className="h-[73px] border-b border-ink-500 bg-ink-800 px-6 md:px-10 flex items-center justify-end gap-5 shrink-0"
        >
          {/* Profile pill */}
          <div className="h-11 flex items-center gap-3 pl-1.5 pr-4 border border-ink-500 bg-ink-900/40 hover:border-ink-500/80 transition-colors">
            <div className="relative">
              <div className="w-8 h-8 bg-brand text-ink-800 font-display font-bold flex items-center justify-center text-sm">
                {(user.name || user.email)?.[0]?.toUpperCase() || "A"}
              </div>
              <span
                className={`absolute -bottom-1 -right-1 w-3 h-3 border-2 border-ink-800 ${
                  isAdmin ? "bg-brand" : "bg-emerald-400"
                }`}
                title={isAdmin ? "Admin" : "Vendedor"}
              />
            </div>
            <div className="leading-tight min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-ash-100 font-medium truncate max-w-[180px]">
                  {user.name || "Administrador DoSoft"}
                </span>
                <span
                  data-testid="user-role-badge"
                  className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 shrink-0 ${
                    isAdmin ? "bg-brand/15 text-brand border border-brand/30" : "bg-emerald-400/10 text-emerald-300 border border-emerald-400/30"
                  }`}
                >
                  {isAdmin ? "Admin" : "Vendedor"}
                </span>
              </div>
              <div className="text-[11px] text-ash-500 font-mono truncate max-w-[240px]">{user.email}</div>
            </div>
          </div>

          {/* Logout */}
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); nav("/admin/login"); }}
            title="Cerrar sesión"
            className="h-11 inline-flex items-center gap-2 border border-ink-500 hover:border-brand hover:text-brand px-4 text-[11px] uppercase tracking-widest text-ash-300 transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} />
            <span className="hidden md:inline">Salir</span>
          </button>
        </header>

        <div className="flex-1">
          <Outlet />
        </div>

        <footer className="border-t border-ink-500 py-5 text-center">
          <div className="text-xs text-ash-500 font-mono">
            © DoSoft · {new Date().getFullYear()}
          </div>
        </footer>
      </main>
    </div>
  );
}
